import { Point } from "openseadragon";
import { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";

import { User } from "pwapi";

import {
  MeasurementAnnotation,
  ScaleInfo,
  ViewerMode,
} from "custom-models/ViewerTypes";

import { changeViewerMode } from "shared/actions/slideDetailActions";
import { createMeasurementAnnotation } from "shared/utils/annotations";

import Measurement from "./Measurement";
import ViewerFacade from "../ViewerFacade";

interface MeasurementToolProps {
  scaleInfo: ScaleInfo;
  viewerFacade: ViewerFacade;
  user: User;
  onAnnotationComplete: (ann: MeasurementAnnotation) => void;
}

const MeasurementTool = (props: MeasurementToolProps) => {
  const dispatch = useDispatch();

  const onAnnotationComplete = useCallback(
    (annotation) => {
      props.onAnnotationComplete(annotation);
    },
    [props.onAnnotationComplete]
  );

  const [annotation, setAnnotation] = useState<MeasurementAnnotation>();

  useEffect(() => {
    const outsideImage = (point: Point) =>
      point.x < 0 ||
      point.y < 0 ||
      point.x > props.scaleInfo.width ||
      point.y > props.scaleInfo.height;

    const clickHandlerId = props.viewerFacade.addHandler(
      "canvas-click",
      (event) => {
        const point = props.viewerFacade.imageCoords(event);
        if (point) {
          setAnnotation((annotation) => {
            if (!annotation) {
              if (!outsideImage(point)) {
                return createMeasurementAnnotation(
                  undefined,
                  "",
                  props.user,
                  null,
                  point,
                  point,
                  ""
                );
              }
            } else {
              const point1 = annotation.point1;
              if (point1 && !outsideImage(point)) {
                const pixelsPerMeter = props.viewerFacade.getPixelsPerMeter();
                let label = "";
                if (pixelsPerMeter) {
                  const distanceInMM =
                    new Point(point1.x, point1.y).distanceTo(
                      new Point(point.x, point.y)
                    ) /
                    (pixelsPerMeter / 1e6) /
                    1000;
                  label = `${distanceInMM.toFixed(2)} millimeters`;
                }
                if (point1.x !== point.x || point1.y !== point.y) {
                  return createMeasurementAnnotation(
                    undefined,
                    annotation.name,
                    props.user,
                    null,
                    point1,
                    point,
                    label
                  );
                }
              }
            }
            return annotation;
          });
        }
      }
    );

    return () => {
      props.viewerFacade.removeHandler(clickHandlerId);
    };
  }, [
    props.scaleInfo.width,
    props.scaleInfo.height,
    props.viewerFacade,
    props.user,
  ]);

  useEffect(() => {
    if (annotation && annotation.point1 !== annotation.point2) {
      onAnnotationComplete(annotation);
      setAnnotation(undefined);
      dispatch(changeViewerMode(ViewerMode.PAN_ZOOM));
    }
  }, [annotation]);

  return (
    <>
      <g
        x={0}
        y={0}
        width={props.scaleInfo.width}
        height={props.scaleInfo.height}
      />
      {annotation && (
        <Measurement
          annotation={annotation}
          viewerFacade={props.viewerFacade}
          scaleInfo={props.scaleInfo}
        />
      )}
    </>
  );
};

export default MeasurementTool;
